import { ArrowUpRight, Check } from "lucide-react";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { FormationPrice, formationOrderHref } from "@/components/formation/FormationHome";
import { FORMATION_5D, FORMATION_FAQ, FORMATION_FOR, FORMATION_INCLUDED, FORMATION_PATH, FORMATION_PRICE } from "@/lib/formation";
import { useSeo, SITE_URL, ORGANIZATION, FOUNDER } from "@/lib/seo";
import { FormationGuidesNav } from "@/components/formation/FormationGuideCta";

const CRUMBS = [{ to: "/", label: "Accueil" }, { to: FORMATION_PATH, label: "Formation" }];
const TITLE = "Formation cheveux afro en ligne : Méthode Ozentya | Ozentya";
const DESC = "Formation en ligne de Pascal Chaubo pour comprendre le cheveu afro, diagnostiquer ses besoins et construire des routines adaptées : 47 leçons, 7 modules, fiches pro, accès à vie.";

function Cta({ src, testid }) {
  return (
    <a href={formationOrderHref(src)} target="_blank" rel="noopener" data-testid={testid} className="group inline-flex h-12 items-center gap-2 rounded-full bg-[var(--gold)] px-7 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] transition-transform duration-300 hover:-translate-y-0.5">
      Je rejoins la formation <ArrowUpRight className="h-4 w-4" />
    </a>
  );
}

export default function FormationPage() {
  const live = Date.now() < FORMATION_PRICE.until;
  useSeo({
    title: TITLE, description: DESC, path: FORMATION_PATH,
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "Course", "@id": `${SITE_URL}${FORMATION_PATH}#course`, name: "Méthode Ozentya : formation cheveux afro en ligne", description: DESC, inLanguage: "fr-FR",
        provider: { "@id": ORGANIZATION["@id"] }, author: FOUNDER, url: "https://ozentyaformation.com",
        hasCourseInstance: { "@type": "CourseInstance", courseMode: "online" },
        ...(live ? { offers: { "@type": "Offer", price: FORMATION_PRICE.now, priceCurrency: "EUR", category: "Paid", url: "https://ozentyaformation.com/commander?offre=essentiel" } } : {}) },
      { "@type": "FAQPage", mainEntity: FORMATION_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
      breadcrumbLd(SITE_URL, CRUMBS),
    ] },
  });
  return (
    <main data-testid="formation-page" className="mx-auto max-w-[1100px] px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb items={CRUMBS} />
      <p className="eyebrow mb-6">Formation en ligne · Méthode Ozentya</p>
      <h1 data-testid="formation-title" className="font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Devenez experte de votre cheveu afro, <span className="italic text-[var(--gold-soft)]">ou de celui de vos clientes</span></h1>
      <p className="mt-6 max-w-3xl text-base text-[var(--parchment)]/90">15 ans d'expérience dans la cosmétique afro transformés en une méthode pratique par Pascal Chaubo, fondateur d'Ozentya : comprendre, diagnostiquer et construire une routine adaptée. 100 % en ligne, à votre rythme, sur ordinateur et mobile.</p>
      <FormationPrice className="mt-8" testid="formation-page-price" />
      <div className="mt-7"><Cta src="page-formation-haut" testid="formation-page-cta-top" /></div>

      <h2 className="mt-20 font-display text-3xl">Pour qui ?</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FORMATION_FOR.map(([t, d]) => <div key={t} className="rounded-sm border border-[var(--line)] p-5"><h3 className="font-semibold">{t}</h3><p className="mt-2 text-sm text-[var(--parchment)]/85">{d}</p></div>)}
      </div>

      <h2 className="mt-20 font-display text-3xl">La méthode 5D</h2>
      <ol className="mt-6 grid gap-4 sm:grid-cols-5">
        {FORMATION_5D.map(([t, d], i) => <li key={t} className="rounded-sm bg-[var(--ink-2)] p-4"><span className="font-display text-3xl text-[var(--gold-soft)]">{i + 1}</span><h3 className="mt-1 text-sm font-semibold uppercase tracking-[0.14em]">{t}</h3><p className="mt-1 text-sm text-[var(--parchment)]/85">{d}</p></li>)}
      </ol>
      <p className="mt-4 text-sm italic text-[var(--parchment)]/85">« Vous ne conseillez plus au hasard : vous suivez une méthode. »</p>

      <h2 className="mt-20 font-display text-3xl">Ce que vous recevez</h2>
      <ul className="mt-6 grid gap-3 sm:grid-cols-2">
        {FORMATION_INCLUDED.map((t) => <li key={t} className="flex gap-2 text-sm text-[var(--parchment)]/90"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" />{t}</li>)}
      </ul>

      <h2 className="mt-20 font-display text-3xl">Questions fréquentes</h2>
      <dl className="mt-6 space-y-5">
        {FORMATION_FAQ.map((f) => <div key={f.q}><dt className="font-semibold">{f.q}</dt><dd className="mt-1 text-sm text-[var(--parchment)]/85">{f.a}</dd></div>)}
      </dl>

      <div className="mt-16 rounded-sm border border-[var(--gold)]/40 bg-[#141013] p-6 md:p-10">
        <p className="font-display text-2xl md:text-3xl">Prête à comprendre enfin votre cheveu ?</p>
        <FormationPrice className="mt-4" testid="formation-page-price-bottom" />
        <div className="mt-6"><Cta src="page-formation-bas" testid="formation-page-cta-bottom" /></div>
        <p className="mt-4 text-xs text-[var(--parchment)]/80">Inscription et paiement sécurisé sur ozentyaformation.com. Approche cosmétique : la formation ne remplace pas un avis médical.</p>
      </div>
      <FormationGuidesNav current={FORMATION_PATH} />
    </main>
  );
}
